import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation, useRoute, Link } from "wouter";
import { Business, MarketplaceItem, businessCategories } from "@shared/schema";
import { BusinessReview } from "@shared/schemas/businesses";
import { koreanRestaurantMenuTypes } from "@shared/business-food-tags";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest, unwrapPayload } from "@/lib/queryClient";
import { tryOpenKakaoShareSheet } from "@/lib/kakao-share";
import {
  Star,
  Phone,
  Mail,
  Globe,
  MapPin,
  Clock,
  Facebook,
  Instagram,
  MessageCircle,
  ExternalLink,
  Share2,
  Building2,
  ShoppingBag,
  CheckCircle2,
  User,
  Edit,
  Settings,
  Trash2,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { PageContainer } from "@/components/layout/PageContainer";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BusinessInquiryManagement } from "@/components/business/BusinessInquiryManagement";
import { BusinessHoursDisplay } from "@/components/business/BusinessHoursDisplay";
import { BusinessGallery } from "@/components/business/BusinessGallery";
import { ApprovedBusinessMedia } from "@/components/business/ApprovedBusinessMedia";
import { GooglePlacesPanel } from "@/components/business/GooglePlacesPanel";
import { BusinessProductCard } from "@/components/business/BusinessProductCard";
import { BusinessProfileHero } from "@/components/business/BusinessProfileHero";
import { BusinessInfoCard } from "@/components/business/BusinessInfoCard";
import { BusinessStorefrontActions } from "@/components/business/BusinessStorefrontActions";
import { trackEvent } from "@/lib/analytics";
import {
  buildGoogleMapsEmbedUrl,
  buildGoogleMapsSearchUrl,
} from "@/lib/google-maps-links";

type BusinessDetail = Business & {
  ownerUserId?: string | number | null;
  ownerPhotoUrl?: string | null;
  ownerNickname?: string | null;
  ownerDisplayName?: string | null;
  ownerProvider?: string | null;
  claimStatus?: "unclaimed" | "claim_pending" | "claimed" | "disputed" | "revoked";
  googlePlaceId?: string | null;
  googleLocation?: { latitude: number; longitude: number } | null;
};

export default function BusinessDirectoryDetail() {
  const [match, params] = useRoute<{ id: string }>("/business-directory/:id");
  const { user, membershipLevel, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewContent, setReviewContent] = useState("");
  const [reviewTitle, setReviewTitle] = useState("");
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [menuExpanded, setMenuExpanded] = useState(false);
  const [hasGoogleHours, setHasGoogleHours] = useState(false);

  // Route transitions can briefly make this component unmatched. Keep every
  // hook call stable and let the router render the next page after the hooks.
  const businessId = params?.id ?? "";

  // Fetch current user info to check ownership
  const { data: userInfo } = useQuery({
    queryKey: ["me", user?.uid],
    queryFn: async () => {
      if (!user) return null;
      const token = await user.getIdToken();
      if (!token) return null;
      const res = await fetch("/api/me", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!user && isAuthenticated,
  });

  // Fetch business details
  const { data: business, isLoading } = useQuery<BusinessDetail>({
    queryKey: [`/api/business-directory/${businessId}`],
    enabled: !!businessId,
  });

  // Fetch reviews
  const { data: reviews = [] } = useQuery<BusinessReview[]>({
    queryKey: [`/api/business-directory/${businessId}/reviews`],
    enabled: !!businessId,
  });

  // Fetch marketplace items from this business
  const { data: marketplaceItems = [] } = useQuery<MarketplaceItem[]>({
    queryKey: ["/api/marketplace/items", "business", businessId],
    queryFn: async () => {
      const response = await fetch(`/api/marketplace/items`);
      if (!response.ok) throw new Error("Failed to fetch items");
      const items = await response.json() as MarketplaceItem[];
      return (items as any[])?.filter((item: MarketplaceItem) => item.businessId === businessId && item.status === "active");
    },
    enabled: !!businessId && !!business?.hasProducts,
  });

  // Fetch business products/services
  const { data: products = [] } = useQuery<any[]>({
    queryKey: [`/api/businesses/${businessId}/products`],
    queryFn: async () => {
      const response = await fetch(`/api/businesses/${businessId}/products`);
      if (!response.ok) {
        // 404는 상품이 없는 것이므로 빈 배열 반환
        if (response.status === 404) return [];
        throw new Error("Failed to fetch products");
      }
      const data = unwrapPayload<{ items: any[] }>(await response.json());
      return data.items || [];
    },
    enabled: !!businessId && business?.claimStatus === "claimed",
    retry: false, // 404는 재시도하지 않음
  });

  // Track view count - only once per page load
  const [viewTracked, setViewTracked] = useState(false);

  // Track view on mount - only once
  useEffect(() => {
    if (businessId && !viewTracked) {
      void trackEvent({
        event: "business_detail_view",
        contentType: "business",
        contentId: businessId,
      });
      fetch(`/api/business-directory/${businessId}/views`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user?.uid || null }),
      })
        .then(() => {
          setViewTracked(true);
        })
        .catch((error) => {
          // Silently fail - don't show error to user for view tracking
          console.error('[BusinessDirectoryDetail] View tracking error:', error);
        });
    }
  }, [businessId]); // Only run when businessId changes

  useEffect(() => {
    setShowReviewForm(false);
  }, [businessId]);

  // Create review mutation
  const createReviewMutation = useMutation({
    mutationFn: async (reviewData: { rating: number; title: string; content: string }) => {
      if (!user) throw new Error("인증이 필요합니다");
      const token = await user.getIdToken();
      return apiRequest("POST", `/api/business-directory/${businessId}/reviews`, {
        rating: reviewData.rating,
        title: reviewData.title || undefined,
        content: reviewData.content,
      }, {
        Authorization: `Bearer ${token}`,
      });
    },
    onSuccess: () => {
      toast({
        title: "리뷰가 등록되었습니다",
        description: "리뷰를 작성해 주셔서 감사합니다",
      });
      setReviewRating(5);
      setReviewContent("");
      setReviewTitle("");
      setShowReviewForm(false);
      queryClient.invalidateQueries({ queryKey: [`/api/business-directory/${businessId}/reviews`] });
      queryClient.invalidateQueries({ queryKey: [`/api/business-directory/${businessId}`] });
    },
    onError: (error: any) => {
      toast({
        title: "리뷰 등록 실패",
        description: error.message || "다시 시도해 주세요",
        variant: "destructive",
      });
    },
  });

  const claimMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("로그인이 필요합니다");
      const token = await user.getIdToken();
      const response = await fetch(`/api/businesses/${encodeURIComponent(businessId)}/claims`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          verificationMethod: "manual_review",
          note: "업체 상세 페이지에서 소유권 요청",
        }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null) as { error?: string } | null;
        throw new Error(payload?.error || "소유권 요청을 접수하지 못했습니다");
      }
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "소유권 요청 접수", description: "관리자가 업체 정보와 신청 내용을 확인합니다." });
      setLocation(`/business-directory/${businessId}/claim`);
    },
    onError: (error: Error) => {
      toast({ title: "소유권 요청 실패", description: error.message, variant: "destructive" });
    },
  });

  const handleSubmitReview = () => {
    if (!reviewContent.trim()) {
      toast({
        title: "내용을 입력해주세요",
        variant: "destructive",
      });
      return;
    }

    createReviewMutation.mutate({
      rating: reviewRating,
      title: reviewTitle,
      content: reviewContent,
    });
  };

  const handleContactClick = (type: string) => {
    const event =
      type === "phone"
        ? "phone_click"
        : type === "website"
        ? "website_click"
        : null;
    if (event) {
      void trackEvent({ event, contentType: "business", contentId: businessId });
    }
    if (businessId && user) {
      fetch(`/api/business-directory/${businessId}/clicks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user.uid }),
      });
    }
  };

  // ③B "카카오톡으로 공유" (16장): try Kakao Talk's native share sheet first,
  // fall back to the existing navigator.share/clipboard behavior when the
  // Kakao JS key isn't configured or the Share module isn't available.
  const handleShare = async () => {
    const title = business?.businessNameKo || business?.businessName || "OZK 업체";
    const opened = tryOpenKakaoShareSheet({
      path: `/business-directory/${businessId}`,
      contentType: "business",
      title,
      description: business?.shortDescription || undefined,
      imageUrl: business?.coverImageUrl || undefined,
      ctaLabel: "업체 정보 보기",
    });
    if (opened) return;

    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast({ title: "링크가 복사되었습니다" });
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      toast({ title: "공유 링크를 준비하지 못했습니다", variant: "destructive" });
    }
  };

  const getCategoryLabel = (categoryId: string) => {
    return businessCategories.find(c => c.id === categoryId)?.label || categoryId;
  };

  // 연락처 정보는 로그인 없이도 모두 볼 수 있도록 변경
  const canViewContactInfo = true;
  // 로그인한 회원이면 리뷰 작성 가능
  const canWriteReview = isAuthenticated;

  // 소유자 확인: user.email이 philip@wemakerobot.com이거나 ownerUserId가 userInfo.id와 일치하는 경우
  const isOwner = isAuthenticated && (
    user?.email === "philip@wemakerobot.com" ||
    (userInfo?.id && (business?.ownerUserId || business?.userId) && String(userInfo.id) === String(business.ownerUserId || business.userId))
  );

  const googleMapsPlace = business ? {
    name: business.businessName || business.businessNameKo,
    address: business.address,
    latitude: business.googleLocation?.latitude ?? business.latitude,
    longitude: business.googleLocation?.longitude ?? business.longitude,
    placeId: business.googlePlaceId,
  } : {};
  const googleMapsUrl = buildGoogleMapsSearchUrl(googleMapsPlace);
  const googleMapsEmbedUrl = buildGoogleMapsEmbedUrl(googleMapsPlace);
  const visibleProducts = products.filter((product) => product.is_available !== false);

  if (!match || !params) return null;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">로딩 중...</div>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">업체를 찾을 수 없습니다</h2>
          <Link href="/business-directory">
            <Button variant="outline" data-testid="button-back">
              목록으로 돌아가기
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <PageContainer size="default">
        {/* Back Button */}
        <Link href="/business-directory">
          <Button variant="outline" className="mb-6" data-testid="button-back">
            ← 목록으로
          </Button>
        </Link>

        <BusinessProfileHero
          name={business.businessNameKo || business.businessName}
          englishName={business.businessName}
          description={business.shortDescription}
          coverImageUrl={business.coverImageUrl}
          categoryLabel={getCategoryLabel(business.category)}
          foodTags={business.category === "restaurant" ? (((business as Business & { foodTags?: string[] }).foodTags) || []).map((tag) => koreanRestaurantMenuTypes.find((type) => type.id === tag)?.label).filter(Boolean) as string[] : []}
          verified={business.isVerified}
          googleLinked={(business as any).providerLinkStatus === "linked"}
          membershipTier={business.membershipTier}
          phone={business.contactPhone}
          website={business.website}
          isOwner={isOwner}
          onPhone={() => { handleContactClick("phone"); if (business.contactPhone) window.location.href = `tel:${business.contactPhone}`; }}
          onDirections={() => { void trackEvent({ event: "directions_click", contentType: "business", contentId: businessId }); if (googleMapsUrl) window.open(googleMapsUrl, "_blank"); }}
          onShare={handleShare}
          onManage={() => { window.location.href = `/businesses/${businessId}/manage`; }}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <BusinessStorefrontActions businessId={businessId} />

            {/* Image Gallery */}
            {(() => {
              // 갤러리 이미지 구성: coverImageUrl이 있으면 포함, imageUrls가 있으면 추가
              const galleryImages: string[] = [];

              // coverImageUrl이 있으면 갤러리에 포함
              if (business.coverImageUrl) {
                galleryImages.push(business.coverImageUrl);
              }

              // imageUrls 배열이 있고 비어있지 않으면 추가
              if ((business as any).imageUrls && Array.isArray((business as any).imageUrls) && (business as any).imageUrls.length > 0) {
                (business as any).imageUrls.forEach((url: string) => {
                  if (url && url !== business.coverImageUrl && !galleryImages.includes(url)) {
                    galleryImages.push(url);
                  }
                });
              }

              // ownerPhotoUrl도 갤러리에 포함 (다른 경우에만)
              if (business.ownerPhotoUrl && business.ownerPhotoUrl !== business.coverImageUrl && !galleryImages.includes(business.ownerPhotoUrl)) {
                galleryImages.push(business.ownerPhotoUrl);
              }

              // coverImageUrl이 있으면 최소한 갤러리 표시 (이미지가 1개 이상이면 표시)
              if (galleryImages.length > 0) {
                return (
                  <BusinessGallery
                    images={galleryImages}
                    title="갤러리"
                  />
                );
              }

              return null;
            })()}
            <ApprovedBusinessMedia businessId={businessId} />
            {(business as any).providerLinkStatus === "linked" && <GooglePlacesPanel businessId={businessId} onHoursAvailabilityChange={setHasGoogleHours} />}

            {/* 인증된 업체가 직접 등록한 메뉴만 하단 버튼에서 펼쳐 표시 */}
            {business.claimStatus === "claimed" && visibleProducts.length > 0 && (
              <Card className="border-primary/20">
                <CardContent className="p-5">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-between"
                    aria-expanded={menuExpanded}
                    onClick={() => setMenuExpanded((value) => !value)}
                  >
                    <span className="flex items-center gap-2"><ShoppingBag className="h-5 w-5" />{business.category === "restaurant" ? "인증 업체 메뉴 보기" : "인증 업체 상품·서비스 보기"}</span>
                    <span className="text-xs text-muted-foreground">{visibleProducts.length}개 · {menuExpanded ? "접기" : "열기"}</span>
                  </Button>
                  {menuExpanded && (
                    <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {visibleProducts.map((product) => <BusinessProductCard key={product.id} product={product} />)}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Description */}
            {business.description && (
              <Card>
                <CardHeader>
                  <CardTitle>업체 소개</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="whitespace-pre-wrap">{business.description}</p>
                  {business.descriptionKo && business.descriptionKo !== business.description && (
                    <>
                      <Separator className="my-4" />
                      <p className="whitespace-pre-wrap text-muted-foreground">{business.descriptionKo}</p>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Owner Information */}
            {(business.ownerNickname || business.ownerDisplayName) && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    소유자
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">
                      {/* meetings 패턴: nickname_display 우선, 그 다음 display_name */}
                      {business.ownerNickname || business.ownerDisplayName || "익명"}
                    </span>
                    {business.ownerProvider && (
                      <Badge variant="outline" className="text-xs">
                        {business.ownerProvider === "kakao" ? "카카오" :
                         business.ownerProvider === "google" ? "구글" :
                         business.ownerProvider}
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Marketplace Items from this Business */}
            {business.hasProducts && marketplaceItems.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ShoppingBag className="h-5 w-5" />
                    이 업체의 핫샵 상품
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {marketplaceItems.map((item) => (
                      <Link key={item.id} href={`/marketplace/${item.id}`}>
                        <Card className="hover-elevate transition-all cursor-pointer">
                          <div className="flex gap-3 p-3">
                            <img
                              src={item.imageUrl || "https://images.unsplash.com/photo-1542838132-92c53300491e?w=200"}
                              alt={item.titleKo || item.title}
                              className="w-20 h-20 rounded object-cover"
                            />
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-sm truncate">
                                {item.titleKo || item.title}
                              </h4>
                              <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                                {item.description}
                              </p>
                              {item.salePrice && (
                                <p className="text-primary font-bold mt-1">${item.salePrice}</p>
                              )}
                            </div>
                          </div>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

          </div>

          {/* Sidebar */}
          <div className="space-y-4 self-start lg:sticky lg:top-24">
            {!isOwner && (business.claimStatus === "unclaimed" || business.claimStatus === "claim_pending") && (
              <Card className="border-primary/30 bg-primary/5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Building2 className="h-5 w-5 text-primary" />이 업체의 소유자이신가요?
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    소유권 확인 후 업체 정보 수정, 문의 관리, 상품·구독 기능을 사용할 수 있습니다.
                  </p>
                  {business.claimStatus === "claim_pending" ? (
                    isAuthenticated ? (
                      <Link href={`/business-directory/${businessId}/claim`}><Button className="w-full" variant="outline">요청 상태 확인</Button></Link>
                    ) : (
                      <p className="text-xs text-muted-foreground">이미 확인 절차가 진행 중인 업체입니다.</p>
                    )
                  ) : isAuthenticated ? (
                    <Button className="w-full" onClick={() => claimMutation.mutate()} disabled={claimMutation.isPending}>
                      {claimMutation.isPending ? "요청 중..." : "업체 소유권 요청"}
                    </Button>
                  ) : (
                    <Link href="/business-directory/register"><Button className="w-full">로그인 후 소유권 요청</Button></Link>
                  )}
                </CardContent>
              </Card>
            )}

            <BusinessInfoCard
              phone={business.contactPhone}
              email={business.contactEmail}
              website={business.website}
              kakaoId={business.kakaoId}
              address={business.address}
              city={business.city}
              postcode={business.postcode}
              hours={hasGoogleHours ? undefined : ((business as any).openHoursJson || business.businessHours)}
              googleMapsUrl={googleMapsUrl}
              googleMapsEmbedUrl={googleMapsEmbedUrl}
              onPhone={() => handleContactClick("phone")}
              onWebsite={() => handleContactClick("website")}
              onDirections={() => { void trackEvent({ event: "directions_click", contentType: "business", contentId: businessId }); if (googleMapsUrl) window.open(googleMapsUrl, "_blank"); }}
            />

            {/* Business Info (ABN, Dealer Info) */}
            {((business as any).abn || (business as any).dealerTier) && (
              <Card>
                <CardHeader>
                  <CardTitle>사업자 정보</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {(business as any).abn && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">ABN</span>
                      <span className="font-semibold">{(business as any).abn}</span>
                    </div>
                  )}
                  {(business as any).dealerTier && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">딜러 티어</span>
                      <Badge variant="outline">{(business as any).dealerTier}</Badge>
                    </div>
                  )}
                  {(business as any).dealerLicence && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">딜러 라이센스</span>
                      <span className="font-semibold">{(business as any).dealerLicence}</span>
                    </div>
                  )}
                  {(business as any).gstStatus && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">GST 상태</span>
                      <Badge variant="outline">{(business as any).gstStatus}</Badge>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

          </div>
        </div>

        {/* Reviews Section - intentionally placed after all business info/map/hours
            so visitors see the important facts first; reviews (often empty for new
            listings) come last instead of pushing that content down on mobile. */}
        {canViewContactInfo && (
          <Card className="mt-6">
            <CardHeader>
              <div className="flex items-center justify-between gap-3">
                <CardTitle>리뷰 ({reviews.length})</CardTitle>
                {canWriteReview && !showReviewForm && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="min-h-11 sm:min-h-9"
                    onClick={() => setShowReviewForm(true)}
                    data-testid="button-open-review-form"
                  >
                    리뷰 쓰기
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {canWriteReview && showReviewForm && (
                <div className="border rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold">리뷰 작성</h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="min-h-11 sm:min-h-9"
                      onClick={() => setShowReviewForm(false)}
                      data-testid="button-cancel-review-form"
                    >
                      취소
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">별점:</span>
                    <Select
                      value={reviewRating.toString()}
                      onValueChange={(v) => setReviewRating(Number(v))}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[5, 4, 3, 2, 1].map((rating) => (
                          <SelectItem key={rating} value={rating.toString()}>
                            {rating} ⭐
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Input
                    type="text"
                    placeholder="리뷰 제목 (선택사항)"
                    value={reviewTitle}
                    onChange={(e) => setReviewTitle(e.target.value)}
                  />
                  <Textarea
                    placeholder="리뷰 내용을 작성해주세요"
                    value={reviewContent}
                    onChange={(e) => setReviewContent(e.target.value)}
                    rows={4}
                    data-testid="textarea-review"
                  />
                  <Button
                    onClick={handleSubmitReview}
                    disabled={createReviewMutation.isPending}
                    data-testid="button-submit-review"
                  >
                    {createReviewMutation.isPending ? "등록 중..." : "리뷰 등록"}
                  </Button>
                </div>
              )}

              {!canWriteReview && (
                <div className="bg-muted/30 border rounded-lg p-4 text-center">
                  <p className="text-sm text-muted-foreground">
                    리뷰를 작성하려면 <strong>로그인</strong>이 필요합니다
                  </p>
                </div>
              )}

              {/* Reviews List */}
              {reviews.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  아직 리뷰가 없습니다
                </p>
              ) : (
                <div className="space-y-4">
                  {reviews.map((review) => (
                    <div key={review.id} className="border rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Avatar>
                          <AvatarImage src={review.userPhotoUrl || undefined} />
                          <AvatarFallback>
                            {review.userDisplayName?.[0]?.toUpperCase() || "U"}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <p className="font-semibold">{review.userDisplayName}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <div className="flex">
                                  {[...Array(5)].map((_, i) => (
                                    <Star
                                      key={i}
                                      className={`h-4 w-4 ${
                                        i < review.rating
                                          ? "fill-yellow-400 text-yellow-400"
                                          : "text-muted"
                                      }`}
                                    />
                                  ))}
                                </div>
                                <span className="text-sm text-muted-foreground">
                                  {new Date(review.createdAt).toLocaleDateString("ko-KR")}
                                </span>
                              </div>
                            </div>
                          </div>
                          {review.title && (
                            <h4 className="font-semibold mb-1">{review.title}</h4>
                          )}
                          <p className="text-sm whitespace-pre-wrap">{review.content}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
    </PageContainer>
  );
}
